import React from 'react'
import { YMaps, Map, Placemark, Circle } from '@pbe/react-yandex-maps'
import s from './maps.module.css'
import { useGetMapQuery } from '../../../app/slices/sliceAddressApi'
import { IMap } from '../../../app/interface'
import { useAppSelector } from '../../../app/Hooks'
import Searcher from './Searcher'

type Props = {}

const COVERAGE_RADIUS = 1500 

const Maps = (props: Props) => {
  const token = useAppSelector(state => state.login.token)
  
  const { data: mapData, isLoading } = useGetMapQuery(
    { token, total: 2000 },
    { skip: !token }
  )

  const placeMark = mapData && mapData.gateways ? mapData.gateways : []

  const marks = placeMark.map((el: IMap) => {
    const coords: [number, number] = [el.location.latitude, el.location.longitude]
    const status = el.status || 'offline'
    
    let markerColor = 'islands#redCircleDotIcon'
    let circleFillColor = '#e74c3c22'
    let circleStrokeColor = '#c0392b'
    let statusText = 'Не работает'
    let statusColor = 'red'
    let hintText = 'БС нет связи'

    if (status === 'online') {
      markerColor = 'islands#greenCircleDotIcon'
      circleFillColor = '#2ecc7122'
      circleStrokeColor = '#27ae60'
      statusText = 'Работает'
      statusColor = 'green'
      hintText = 'БС в сети'
    } else if (status === 'warning') {
      markerColor = 'islands#orangeCircleDotIcon'
      circleFillColor = '#f39c1222'
      circleStrokeColor = '#d35400'
      statusText = 'Менее суток'
      statusColor = 'orange'
      hintText = 'БС менее суток'
    }
    
    const lastSeenText = el.lastSeen 
      ? new Date(el.lastSeen).toLocaleString('ru-RU') 
      : 'Нет данных'

    let placemarkInstance: any = null

    return (
      <React.Fragment key={el.id}>
        {/* Отрисовываем зону покрытия только если станция РАБОТАЕТ (online или warning) */}
        {status !== 'offline' && (
          <Circle
            geometry={[coords, COVERAGE_RADIUS]}
            options={{
              fillColor: circleFillColor,
              strokeColor: circleStrokeColor,
              strokeOpacity: 0.6,
              strokeWidth: 1,
              interactivityModel: 'default#transparent' 
            }}
          />
        )}

        <Placemark
          geometry={coords}
          properties={{
            hintContent: `${hintText} Координаты: ${coords[0]}, ${coords[1]}`,
            balloonContent: `
              <b>ID станции:</b> ${el.id}<br/>
              <b>Статус:</b> <span style="color: ${statusColor}">${statusText}</span><br/>
              <b>Последний ping:</b> ${lastSeenText}<br/>
              <hr/>
              <b>Координаты:</b> ${coords[0]}, ${coords[1]}<br/>
              <i>Нажмите для загрузки адреса...</i>
            `
          }}
          modules={['geoObject.addon.balloon', 'geoObject.addon.hint']}
          options={{ preset: markerColor }}
          instanceRef={(ref) => {
            if (ref) placemarkInstance = ref
          }}
          onClick={() => {
            const ymaps = (window as any).ymaps
            if (ymaps && ymaps.geocode && placemarkInstance) {
              ymaps.geocode(coords).then((res: any) => {
                const firstGeoObject = res.geoObjects.get(0)
                const address = firstGeoObject ? firstGeoObject.getAddressLine() : 'Адрес не найден'
                
                placemarkInstance.properties.set(
                  'balloonContent',
                  `
                    <b>ID станции:</b> ${el.id}<br/>
                    <b>Статус:</b> <span style="color: ${statusColor}">${statusText}</span><br/>
                    <b>Последний ping:</b> ${lastSeenText}<br/>
                    <hr/>
                    <b>Координаты:</b> ${coords[0]}, ${coords[1]}<br/>
                    <b>Адрес:</b> ${address}
                  `
                )
              }).catch(() => {
                if (placemarkInstance) {
                  placemarkInstance.properties.set(
                    'balloonContent',
                    `<b>Координаты:</b> ${coords[0]}, ${coords[1]}<br/><i>Ошибка геокодера</i>`
                  )
                }
              })
            }
          }}
        />
      </React.Fragment>
    )
  })

  return (
    <>
      {isLoading ? <div>Загрузка...</div> :
        <YMaps query={{ apikey: '81c925a7-49ac-4f33-9d28-baeb61d72c0f', load: 'package.full' }}>
          <div className={s.mapTopBlock}>
            <span className={s.mapTitle}>Карта покрытия базовых станций</span>
            <Searcher />
          </div>

          <div className={s.mapBottomBlock}>
            <div className={s.mapBlockRight}>
              <Map
                width="100%"
                height="100%"
                defaultState={{ 
                  center: [53.89385, 27.60526], 
                  zoom: 11, 
                  controls: ['zoomControl', 'typeSelector'] 
                }}
                options={{ 
                  suppressMapOpenBlock: true,
                  yandexMapDisablePoiInteractivity: true
                }}
                modules={['geoObject.addon.balloon', 'geoObject.addon.hint']}
                onLoad={(ymaps) => { (window as any).ymaps = ymaps }}
                instanceRef={(map) => {
                  if (map) {
                    (window as any).yandexMapInstance = map
                  }
                }}
              >
                {marks}
              </Map>
            </div>
          </div>
        </YMaps>}
    </>
  )
}

export default Maps
